import { FC, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { coreService } from 'network-utils';
import { translate } from 'translations';
import { useSelector } from 'react-redux';
import { selectLabsAccess } from '@redux/slices/userSlice';
import { Button, ContentCard, ContentCardProps, UiLibrary, Tooltip, Icon } from 'learning-panel-components';
import HighlightNewCourses from './highlight/highlightNewCourses';
import styles from './discover.module.scss';
import ContentType from './contentType/contentType';
import Tag from './tag/tag';
import CategoryCard from './category/category';
import MoreTags from './tag/moreTags';

const Discover: FC<DiscoverProps> = (props: DiscoverProps) => {
    const {
        newCourses,
        categories,
        setSelectedCategory,
        selectedCategory,
        selectedContentType,
        setSelectedContentType,
        selectedTags,
        setSelectedTags,
        discoveryData,
        onLoadMore,
        total,
        isLoadingDiscoveryData
    } = props;

    const [selectedCategoryTags, setSelectedCategoryTags] = useState<any[]>([]);
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const ref = useRef() as React.MutableRefObject<HTMLInputElement>;
    const [renderedItems, setRenderedItems] = useState<JSX.Element[]>([]);
    const contentRef = useRef<HTMLDivElement>(null);
    const taggingsContentTypesValues: string[] = [
        'Course',
        'Lesson',
        'Challenge',
        'SkillTree',
        'LearningPath',
        'VideoTutorial'
    ];
    const labsAccess = useSelector(selectLabsAccess);

    if (labsAccess) taggingsContentTypesValues.push('Lab');

    const moreText = translate('More');
    const clearFiltersText = translate('ClearFilters');

    const getItemWidth = (text: string, count: number) => {
        const div = document.createElement('div');
        div.innerText = ` ${text} +  ( ${count} ) `;
        div.style.fontSize = '0.875rem';
        div.style.position = 'absolute';
        div.style.fontFamily = 'inter';
        div.style.visibility = 'hidden';
        document.body.appendChild(div);
        const width = div.offsetWidth;
        document.body.removeChild(div);
        return width;
    };

    const onApplyMore = (tags: string[]) => {
        const firstTags = selectedCategoryTags.slice(0, renderedItems.length - 2);
        const selectedFirstTags = firstTags.filter(tag => selectedTags.includes(tag.name)).map(tag => tag.name);
        setSelectedTags([...selectedFirstTags, ...tags]);
        setAnchorEl(null);
    };

    const handleResize = () => {
        if (ref.current) {
            let totalWidth = 0;
            const renderedItems: JSX.Element[] = [];
            selectedCategoryTags.every((tag, index) => {
                const itemWidth = getItemWidth(tag.displayName, tag.count);
                const marginWidth = 20;
                const moreWidth = getItemWidth(moreText, selectedCategoryTags.length - renderedItems.length);
                const totalItemWidth = itemWidth;
                if (totalWidth + totalItemWidth + marginWidth <= ref.current!.offsetWidth - moreWidth) {
                    renderedItems.push(
                        <Tag
                            key={tag.name}
                            name={tag.displayName}
                            count={tag.count}
                            onClick={() => setTags(tag.name)}
                            isSelected={selectedTags.includes(tag.name)}
                        />
                    );
                    totalWidth += totalItemWidth + marginWidth;
                    return true;
                }
                return false;
            });
            if (renderedItems.length < selectedCategoryTags.length) {
                renderedItems.push(
                    <span>
                        <Tag
                            key="more"
                            name={moreText}
                            count={selectedCategoryTags.length - renderedItems.length}
                            onClick={(e: React.MouseEvent<HTMLDivElement>) => setAnchorEl(e.currentTarget)}
                            isSelected={!!anchorEl}
                            showExpandMoreIcon
                        />
                        <UiLibrary.Popover
                            open={!!anchorEl}
                            anchorEl={anchorEl}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                            className={styles.popover}
                        >
                            <UiLibrary.ClickAwayListener onClickAway={() => setAnchorEl(null)}>
                                <div>
                                    <MoreTags
                                        onApply={(tags: string[]) => onApplyMore(tags)}
                                        onClose={() => setAnchorEl(null)}
                                        selectedTags={selectedCategoryTags
                                            .slice(renderedItems.length)
                                            .filter(tag => selectedTags.includes(tag.name))
                                            .map(tag => tag.name)}
                                        tags={selectedCategoryTags.slice(renderedItems.length).map(tag => ({
                                            count: tag.count,
                                            displayName: tag.displayName,
                                            name: tag.name
                                        }))}
                                    />
                                </div>
                            </UiLibrary.ClickAwayListener>
                        </UiLibrary.Popover>
                    </span>
                );
            }
            selectedTags &&
                selectedTags.length > 0 &&
                renderedItems.push(
                    <Tooltip title={clearFiltersText}>
                        <span>
                            <Icon
                                onClick={() => setSelectedTags([])}
                                name="deleteEvent"
                                className={styles['delete-icon']}
                            />
                        </span>
                    </Tooltip>
                );
            setRenderedItems(renderedItems);
        }
    };

    useEffect(() => {
        handleResize();
        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [selectedCategoryTags.length, selectedTags.length, anchorEl]);

    useEffect(() => {
        if (selectedCategory === '') {
            setSelectedCategoryTags([]);
        } else {
            setSelectedCategoryTags(
                categories
                    .find(category => category.categoryName === selectedCategory)
                    ?.tags.sort((tag1, tag2) => tag2.count - tag1.count) || []
            );
        }
    }, [selectedCategory]);

    const setCategory = (category: string) => {
        if (selectedCategory === category) {
            setSelectedCategory('');
            setSelectedCategoryTags([]);
        } else {
            setSelectedCategory(category);
            contentRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        }
    };

    const contentTypeClick = (type: string) => {
        setSelectedContentType((prev: string | null) => (prev === type ? [] : type));
    };

    const setTags = (tag: string) => {
        if (selectedTags.includes(tag)) {
            setSelectedTags(selectedTags.filter(t => t !== tag));
        } else {
            setSelectedTags([...selectedTags, tag]);
        }
    };

    const clearFilters = () => {
        setSelectedCategory('');
        setSelectedContentType('');
        setSelectedTags([]);
    };

    return (
        <div id="discover-page" className={styles.content}>
            {newCourses.length > 0 && <HighlightNewCourses courses={newCourses} />}
            <div className={styles.top}>
                <div className={styles.right}>
                    <img
                        className={styles.img}
                        alt="cbLogoBot"
                        src={`${process.env.PUBLIC_URL}/images/cxSmallLogo.svg`}
                    />
                    <div>{translate('EnjoyContentBy')}</div>
                </div>
            </div>
            <div className={styles.categories}>
                {categories.map((category, index) => (
                    <CategoryCard
                        key={index}
                        name={category.categoryName}
                        imageSrc={category.iconUrl}
                        onClick={() => setCategory(category.categoryName)}
                        isSelected={selectedCategory === category.categoryName}
                    />
                ))}
            </div>
            <div className={styles['content-filters']}>
                <div className={styles['content-types-tags']}>
                    <div className={styles['filters-title']}>{translate('ContentType')}</div>
                    <div className={styles['content-types']}>
                        {taggingsContentTypesValues.map(type => (
                            <ContentType
                                key="type"
                                type={type.toLocaleLowerCase() as any}
                                onClick={() => contentTypeClick(type)}
                                isSelected={selectedContentType.includes(type as any)}
                            />
                        ))}
                    </div>
                </div>
                {selectedCategoryTags.length > 0 && <hr />}
                <div
                    className={classNames(styles['content-tags'], {
                        [styles['hidden-tags']]: !selectedCategoryTags.length
                    })}
                >
                    <div className={styles['filters-title']}>{translate('FilterBy')}</div>
                    <div ref={ref} className={styles.tags}>
                        {renderedItems}
                    </div>
                </div>
            </div>
            {discoveryData?.length === 0 && !isLoadingDiscoveryData && (
                <div className={styles['empty-state']} ref={contentRef}>
                    <div className={styles.title}>{translate('NoResultsForThisFilterCombination')}</div>
                    <div className={styles['sub-title']}>{translate('TryAdifferentOne')}</div>
                    <Button
                        onClick={() => clearFilters()}
                        text={translate('ClearFilters')}
                        type="gradientNoBorder"
                        lowerCase
                    />
                    <img
                        className={styles.img}
                        alt="noResultsImage"
                        src={`${process.env.PUBLIC_URL}/images/noResult.svg`}
                    />
                </div>
            )}
            {discoveryData?.length > 0 && (
                <div className={styles.list} ref={contentRef}>
                    <UiLibrary.Grid
                        container
                        direction="row"
                        justifyContent="flex-start"
                        alignItems="center"
                        spacing={3}
                    >
                        {discoveryData.map((card: ContentCardProps, index: number) => (
                            <UiLibrary.Grid item key={index} style={{ display: 'flex', justifyContent: 'center' }}>
                                <ContentCard {...card} key={`${card.type}${card.uuid}`} />
                            </UiLibrary.Grid>
                        ))}
                    </UiLibrary.Grid>
                </div>
            )}
            {discoveryData?.length > 0 && discoveryData.length < total && (
                <div className={styles['load-more']}>
                    {translate('viewingXFromAll', { length: String(discoveryData.length), total: String(total) })}
                    <Button
                        onClick={() => onLoadMore()}
                        text={translate('LoadMore')}
                        type="gradientNoBorder"
                        lowerCase
                    />
                </div>
            )}
        </div>
    );
};

export default Discover;

interface DiscoverProps {
    newCourses: coreService.CourseHighlight[];
    isLoadingCategories: boolean;
    isLoadingNewCourses: boolean;
    isLoadingDiscoveryData: boolean;
    categories: coreService.CategoryWithTags[];
    setSelectedCategory: Function;
    selectedCategory: string;
    selectedContentType: string[];
    setSelectedContentType: Function;
    selectedTags: string[];
    setSelectedTags: Function;
    discoveryData: ContentCardProps[];
    onLoadMore: Function;
    total: number;
}
